import styled from 'styled-components';

export const Container = styled.div`
  font-family: 'Sora', sans-serif;
  padding: 50px;
  display: flex;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.white};

  * {
    letter-spacing: 0px;
    line-height: initial;
  }

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoints.xxl}px) {
    padding: 30px;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    padding: 20px;
  }
`;

export const Wrapper = styled.div`
  max-width: 1500px;
  width: 100%;
  position: relative;

  > header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    padding-bottom: 10px;
    gap: 20px;

    @media only screen and (max-width: ${({ theme }) =>
        theme.breakpoints.lg}px) {
      flex-direction: column;
      align-items: stretch;
      text-align: left;
    }

    h1 {
      font-weight: 700;
      color: ${({ theme }) => theme.colors.black};
      font-size: 28px;
      letter-spacing: 0px;
    }

    h3 {
      color: ${({ theme }) => theme.colors.grey};
      font-weight: 400;
      padding-top: 5px;
    }
  }
`;

export const PeriodSelect = styled.div`
  display: flex;
  flex-direction: column;

  label {
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.grey};
    padding-bottom: 6px;
  }

  select {
    font-family: 'Sora', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.softBlack};
    border: 3px solid ${({ theme }) => theme.colors.lightGrey};
    border-radius: 5px;
    padding: 12px 15px;
    background-color: ${({ theme }) => theme.colors.white};
    cursor: pointer;
  }
`;

export const Cards = styled.main`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 30px;
  padding-top: 30px;

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoints.xxl}px) {
    grid-gap: 20px;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    grid-gap: 20px;
    grid-template-columns: repeat(2, 1fr);
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

export const CardItem = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 150px;
  padding: 20px;
  border: 3px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 5px;

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    svg {
      font-size: 24px;
    }

    h2 {
      font-size: 15px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.grey};
    }
  }

  main {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 14px 0 6px;

    h3 {
      font-weight: 700;
      color: ${({ theme }) => theme.colors.softBlack};
      font-size: 38px;
    }
  }

  footer {
    span {
      font-size: 13px;
      color: ${({ theme }) => theme.colors.grey};
    }
  }
`;

export const Badge = styled.span<{ tone: 'positive' | 'negative' | 'warning' }>`
  display: inline-block;
  align-self: flex-start;
  font-size: 16px;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme, tone }) => {
    if (tone === 'positive') return theme.colors.green;
    if (tone === 'warning') return theme.colors.orange;
    return theme.colors.red;
  }};
`;

export const Value = styled.h3<{ tone?: 'positive' | 'negative' | 'warning' }>`
  color: ${({ theme, tone }) => {
    if (tone === 'positive') return theme.colors.green;
    if (tone === 'warning') return theme.colors.orange;
    if (tone === 'negative') return theme.colors.red;
    return theme.colors.softBlack;
  }} !important;
`;

export const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 80px;
`;

/** Seções detalhadas do dashboard fiscal (Story 4-3 / T4.8). */
export const Section = styled.section`
  margin-top: 28px;

  h2 {
    font-size: 18px;
    color: ${({ theme }) => theme.colors.black};
    margin-bottom: 12px;
  }
`;

export const EmptyDetail = styled.div`
  padding: 16px;
  border-radius: 8px;
  border: 3px solid ${({ theme }) => theme.colors.lightGrey};
  color: ${({ theme }) => theme.colors.grey};
  font-size: 14px;
`;

export const RejeicaoList = styled.ul`
  list-style: none;
  border: 3px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  overflow: hidden;

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 16px;

    & + li {
      border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
    }

    .motivo {
      color: ${({ theme }) => theme.colors.softBlack};
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .total {
      flex-shrink: 0;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.red};
      min-width: 32px;
      text-align: right;
    }
  }
`;

export const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 3px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  overflow: hidden;

  th,
  td {
    text-align: left;
    padding: 10px 16px;
    font-size: 14px;
  }

  thead th {
    color: ${({ theme }) => theme.colors.grey};
    background-color: ${({ theme }) => theme.colors.white};
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  }

  tbody tr + tr td {
    border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  }

  td {
    color: ${({ theme }) => theme.colors.softBlack};
  }

  td.chave {
    font-size: 12px;
    word-break: break-all;
    color: ${({ theme }) => theme.colors.grey};
  }
`;
